// @flow

import * as React from 'react';

import { NavigableListItem } from '@ta/list.navigable-list';

import type { FetcherResult } from '../Typeahead';
import BasicResult from './BasicResult';
import TypeaheadTracking, { type UserInteractionType } from '../TypeaheadTracking';
import type { TypeaheadResult } from '../TypeaheadResult';

type Props = {|
  /** The GeoThemeFetcher result */
  result: FetcherResult,

  /** The typeahead tracker */
  tracker: TypeaheadTracking,

  /** An optional callback if a result is selected */
  onSelection?: (
    event: SyntheticEvent<HTMLElement> | KeyboardEvent,
    result: TypeaheadResult,
  ) => void,

  /** An optional callback for if/when the mouse moves over the result */
  onMouseMove?: (event: SyntheticMouseEvent<HTMLElement>) => mixed,

  /** True if this result is active. Defaults to false */
  active?: boolean,

  /** result position in result list */
  resultIndex?: number,
|};

/**
 * Create a typeahead search result from a recently viewed API result. Selecting a result of this type will trigger a
 * call to onSelection() with a TypeaheadResult of { type: "geo_theme_location_id", value: <geo_id> }
 *
 * Note that this is intended to be used inside a <NavigableList/>
 */
class GeoThemeResult extends React.Component<Props> {
  static defaultProps = {
    onSelection: undefined,
    onMouseMove: undefined,
    active: false,
    resultIndex: undefined,
  };

  onSelect = (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
    const userInteractionType: UserInteractionType = event.type === 'click' ? 'click' : 'enter';
    const { tracker, onSelection, result, resultIndex } = this.props;
    tracker.trackSelection({ resultIndex, ...result }, userInteractionType);

    const { location_id: geoId } = result;
    if (onSelection) {
      onSelection(event, {
        type: 'geo_theme_location_id',
        value: geoId,
      });
    }
  };

  render() {
    const { name: title } = this.props.result;
    const ancestorCnt = this.props.result.ancestors ? this.props.result.ancestors.length : 0;

    let subtitle;
    if (ancestorCnt > 1) {
      subtitle = `${this.props.result.ancestors[0].name}, ${this.props.result.ancestors[1].name}`;
    } else if (ancestorCnt > 0) {
      subtitle = this.props.result.ancestors[0].name;
    }

    return (
      <NavigableListItem
        active={!!this.props.active}
        onMouseMove={this.props.onMouseMove}
        onSelection={this.onSelect}
      >
        <BasicResult title={title} subtitle={subtitle} active={this.props.active} />
      </NavigableListItem>
    );
  }
}

export default GeoThemeResult;
